// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Modal, Nav, Form, Col, Tab, Row } from "react-bootstrap";

import { DeviceType } from "shared/types/globtypes";
import {
  ChargerColum,
  ChargerReportContent,
  ExtendedPeriod,
  IChargerReportFilter,
  IoReportContent,
  Period,
  Report,
  RfidColum,
  RfidReportContent,
  SiteColum,
  SiteReportContent,
  TrendingColum,
  TrendReportContent,
  prepareIOBusModuleChannel,
} from "shared/types/cloudtypes";
import { getBusNr, IInput, IOutput, ITariffCircuit } from "shared/types/parameter";
import { isSet } from "shared/utils/utils";
import { ActionType, ChangedReport, IAction, IActionTypeChangeReport, raiseAction } from "actions";
import { DataState } from "types";
import { stringEnumToLabels } from "guiutils";
import { IState } from "states/states";
import { ModalPanel } from "states/guistates";
import { IOcppCharger, IRfid } from "states/ocppstates";
import { Company } from "states/sitestates";

import CheckList, { ICheckListOption } from "view/comp/CheckList";
import DateInput from "view/comp/DateInput";
import SaveFeedback from "view/comp/SaveFeedback";

type IModalReportSettingsItemProps = {
  isOpen: boolean;
  userId: number;
  companyList: Company[];
  userCompanyId: number | undefined;
  canCreateCrossSiteReport: boolean;
  canModifyRfids: boolean;
  hasUserAdminRights: boolean;
  userEmail: string | undefined;
  isVirtualSite: boolean;
  siteId: number | undefined;
  reportId: number | undefined; // undefined for new reports
  dataState: DataState;
  oldValue: Report | undefined;
  tariffCircuits: ITariffCircuit[];
  input: IInput[];
  output: IOutput[];
  rfids: IRfid[];
  chargers: IOcppCharger[];
};

const mapStateToProps = (state: IState) => {
  const isOpen = state.gui.modal.panel === ModalPanel.REPORT_SETTINGS;
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);
  if (site === undefined) {
    console.error("admin unidentified report site", siteId, state.site.siteList);
  }
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const oldValue = state.site.reportList?.find((curItem) => curItem.id === state.gui.modalReport);
  const props: IModalReportSettingsItemProps = {
    isOpen,
    userId: curUser?.id ?? 0,
    companyList: state.site.companyList ?? [],
    userCompanyId: curUser?.companyId,
    canCreateCrossSiteReport: curUser?.accessRights.modifySites ?? false,
    // rfid reports are only available to users who can modify the rfid cards
    canModifyRfids: curUser?.accessRights.ocppSettings ?? false,
    hasUserAdminRights: curUser?.accessRights.modifyUsers ?? false,
    // the reports page only lists the reports of the logged-in user, who thus owns them
    userEmail: curUser?.email,
    isVirtualSite: site?.deviceType === DeviceType.VIRTUAL,
    siteId,
    reportId: state.gui.modalReport,
    dataState: state.scheduler.saveState,
    oldValue,
    tariffCircuits: state.para.parameter?.tariffline ?? [],
    rfids: state.ocpp.rfid,
    chargers: state.ocpp.charger,
    input: state.para.parameter?.input ?? [],
    output: state.para.parameter?.output ?? [],
  };
  return props;
};

type IModalReportSettingsState = ChangedReport & {
  wasOpen: boolean;
  isChanged: boolean;
  emptyInputFilter: boolean;
  emptyOutputFilter: boolean;
  // keep the circuit list open when all circuits get unchecked (= no filter)
  isCircuitListOpen: boolean;
};

interface IModalReportSettingsItemDispatch {
  onCancelClick: () => void;
  onSaveClick: (state: IModalReportSettingsState) => void;
  onDeleteClick: (state: IModalReportSettingsState) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalReportSettingsItemDispatch = {
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
    onSaveClick: (state: IModalReportSettingsState) => {
      const payload: IActionTypeChangeReport = { report: extractReport(state) };
      dispatch(raiseAction(ActionType.ACT_CHANGE_REPORT_REQ, payload));
    },
    onDeleteClick: (state: IModalReportSettingsState) => {
      const payload: IActionTypeChangeReport = { report: extractReport(state), isDelete: true };
      dispatch(raiseAction(ActionType.ACT_CHANGE_REPORT_REQ, payload));
    },
  };
  return dispatchProps;
};

// strip the local edit flags from the dialog state
function extractReport(state: IModalReportSettingsState): ChangedReport {
  const { wasOpen, isChanged, emptyInputFilter, emptyOutputFilter, isCircuitListOpen, ...report } =
    state;
  return report;
}

const messages = defineMessages({
  titleDefault: {
    id: "new report",
  },
  titleHint: {
    id: "custom name of report",
  },
  exportTargetHint: {
    id: "email address for report receipt",
  },
  groupByNone: {
    id: "none",
  },
  groupByDate: {
    id: "date",
  },
  groupByCircuit: {
    id: "tariff circuit",
  },
  groupByThen: {
    id: "then",
  },
  groupByCharger: {
    id: "charger",
  },
  groupByRfid: {
    id: "charging card",
  },
  tariffCircuits: {
    id: "tariff circuits",
  },
  inputs: {
    id: "Inputs",
  },
  outputs: {
    id: "Outputs",
  },
});

// shared label width so the file format, delimiter and export period selects line up
const compactLabelStyle: React.CSSProperties = { minWidth: "8rem" };

class ModalReportSettingsItemComponent extends React.Component<
  IModalReportSettingsItemProps & IModalReportSettingsItemDispatch,
  IModalReportSettingsState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IModalReportSettingsState = {
    wasOpen: false,
    isChanged: false,
    emptyInputFilter: false,
    emptyOutputFilter: false,
    isCircuitListOpen: false,
    ...this.makeEmptyReport(this.props.isVirtualSite ? "charger" : "trend"),
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      if (isSet(this.props.reportId)) {
        // load new report data to local state
        this.setState({
          wasOpen: this.props.isOpen,
          isChanged: false,
          isCircuitListOpen: false,
          ...this.props.oldValue!,
        });
      } else {
        // load new empty report
        this.setState({
          wasOpen: this.props.isOpen,
          isChanged: false,
          isCircuitListOpen: false,
          ...this.makeEmptyReport(this.props.isVirtualSite ? "charger" : "trend"),
        });
      }
    }
  }

  public render() {
    if (!this.props.isOpen) return <></>; // avoid rendering of inexisting data

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick}>
        <Modal.Header closeButton>
          <FormattedMessage id="Report Settings" />
        </Modal.Header>
        <Modal.Body>{this.renderSettings()}</Modal.Body>
        <Modal.Footer>
          <SaveFeedback dataState={this.props.dataState} />
          <Button
            id="cancelReportSettings"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          {(this.state.isChanged || this.props.reportId === undefined) && (
            <Button
              id="saveReportSettings"
              variant="primary"
              className="me-1"
              disabled={!this.isSettingsOk()}
              onClick={(e) => this.props.onSaveClick(this.state)}
            >
              <FormattedMessage id="Save" />
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }

  private isSettingsOk() {
    // inhibit auto-export to other recipients without a valid email target;
    // without recipients the export goes to the report owner
    const hasCustomRecipients =
      this.state.exportInterval !== "none" && isSet(this.state.recipients);
    if (
      (hasCustomRecipients && !this.areAllTargetsValid(this.state.recipients)) ||
      (this.state.emptyInputFilter === true && this.state.emptyOutputFilter === true)
    ) {
      return false;
    }
    return true;
  }

  private areAllTargetsValid(target: string | undefined): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!target) return false;
    const addresses = target.split(",").map((curAddress) => curAddress.trim());
    return addresses.length > 0 && addresses.every((curAddress) => emailRegex.test(curAddress));
  }

  private renderSettings() {
    return (
      <>
        <h1>
          {this.state.type === "trend" && <FormattedMessage id="Trend Report" />}
          {this.state.type === "charger" && <FormattedMessage id="Charging Report" />}
          {this.state.type === "io" && <FormattedMessage id="Input/Output Report" />}
          {this.state.type === "site" && <FormattedMessage id="Site Report" />}
          {this.state.type === "rfid" && <FormattedMessage id="Charging Card Report" />}
        </h1>
        <Form.Group className="mb-2">
          <Form.Label htmlFor="title" sm={3}>
            <FormattedMessage id="Title" />
          </Form.Label>
          <Form.Control
            id="title"
            placeholder={this.context.formatMessage(messages.titleHint)}
            type="text"
            value={this.state.title}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                title: event?.currentTarget.value,
              });
            }}
          />
        </Form.Group>
        <Form.Group className="mt-3 mb-3">
          <Col sm={9}>
            <Form.Check
              id="isActive"
              className="mt-2"
              checked={this.state.isActive}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  isActive: event?.currentTarget.checked,
                });
              }}
              label={<FormattedMessage id="Activated" />}
            />
            {!this.state.isActive && isSet(this.props.reportId) && (
              <Button
                style={{ marginTop: -2 }}
                id="deleteReport"
                variant="danger"
                className="ms-3"
                onClick={(_event) => this.props.onDeleteClick(this.state)}
              >
                <FormattedMessage id="Delete Report" />
              </Button>
            )}
          </Col>
        </Form.Group>
        <div>
          {/* the api keeps the type and the scope of a stored report, so only a new
              report may still choose them */}
          {!isSet(this.props.reportId) && (
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link
                  className={this.state.type === "trend" ? "active" : "inactive"}
                  onClick={() => this.changeReportType("trend")}
                >
                  <FormattedMessage id="Trend" />
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  className={this.state.type === "charger" ? "active" : "inactive"}
                  onClick={() => this.changeReportType("charger")}
                >
                  <FormattedMessage id="Charging" />
                </Nav.Link>
              </Nav.Item>
              {this.props.canModifyRfids && (
                <Nav.Item>
                  <Nav.Link
                    className={this.state.type === "rfid" ? "active" : "inactive"}
                    onClick={() => this.changeReportType("rfid")}
                  >
                    <FormattedMessage id="Charging Cards" />
                  </Nav.Link>
                </Nav.Item>
              )}
              {!this.props.isVirtualSite && (
                <Nav.Item>
                  <Nav.Link
                    className={this.state.type === "io" ? "active" : "inactive"}
                    onClick={() => this.changeReportType("io")}
                  >
                    <FormattedMessage id="IOs" />
                  </Nav.Link>
                </Nav.Item>
              )}
              {this.props.canCreateCrossSiteReport && (
                <Nav.Item>
                  <Nav.Link
                    className={this.state.type === "site" ? "active" : "inactive"}
                    onClick={() => this.changeReportType("site")}
                  >
                    <FormattedMessage id="Sites" />
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          )}
          <Tab.Content>
            {this.state.type === "trend" && this.renderTrendSettings()}
            {this.state.type === "charger" && this.renderChargerSettings()}
            {this.state.type === "rfid" && this.renderRfidSettings()}
            {this.state.type === "io" && this.renderIoSettings()}
            {this.state.type === "site" &&
              this.props.canCreateCrossSiteReport &&
              this.renderSiteSettings()}
          </Tab.Content>
        </div>
        {this.renderFileFormat()}
        {this.state.fileFormat === "csv" && this.renderCSVDelimiter()}
        {this.renderHideSumRowCheckbox()}
        {this.renderExport()}
      </>
    );
  }

  // switching the type resets the type specific content, but keeps the general settings
  private changeReportType(reportType: Report["type"]) {
    this.setState({
      isChanged: true,
      ...this.makeEmptyReport(reportType),
      title: this.state.title,
      isActive: this.state.isActive,
      exportInterval: this.state.exportInterval,
      recipients: this.state.recipients,
      fileFormat: this.state.fileFormat,
      csvDelimiter: this.state.csvDelimiter,
      hideSumRow: this.state.hideSumRow,
    });
  }

  private renderTrendSettings() {
    const isEvuTrend = (this.state.content as TrendReportContent).isEvuFormat === true;
    const tariffIndexes = Array.from(Array(this.props.tariffCircuits.length).keys())
      .filter((curId) => this.props.tariffCircuits[curId].webActive)
      .sort((a, b) =>
        this.props.tariffCircuits[a].name.localeCompare(this.props.tariffCircuits[b].name),
      );
    const tariffOptions = tariffIndexes.map((curIndex) => ({
      value: this.props.tariffCircuits[curIndex].id ?? curIndex,
      label: this.props.tariffCircuits[curIndex].name,
    }));
    const tariffItems = tariffOptions.map((curOption) => (
      <option key={curOption.value} value={`${curOption.value}`}>
        {curOption.label}
      </option>
    ));

    return (
      <>
        <Form.Group as={Row} className="mb-3">
          <Col sm={9}>
            <Form.Check
              id="isEvuFormat"
              style={{ marginTop: 9 }}
              checked={isEvuTrend}
              onChange={(event) => {
                // ensure valid settings for EVU format
                const content = { ...this.state.content } as TrendReportContent;
                if (event.currentTarget.checked) {
                  content.columns = [TrendingColum.ENERGY];
                  content.groupBy = ["date", "circuit"];
                }
                this.setState({
                  isChanged: true,
                  content: {
                    ...content,
                    isEvuFormat: event.currentTarget.checked,
                  },
                });
              }}
              label={<FormattedMessage id="EVU Format" />}
            />
          </Col>
        </Form.Group>
        {this.renderDataColumns()}
        {this.renderGrouping()}
        {!isEvuTrend && this.renderTrendReference(tariffItems)}
        {this.renderFilter(tariffOptions)}
      </>
    );
  }

  private renderDataColumns() {
    const isTrend = this.state.type === "trend";
    const isSite = this.state.type === "site";
    const isRfid = this.state.type === "rfid";
    const isEvuTrend = isTrend && (this.state.content as TrendReportContent).isEvuFormat === true;
    const columns =
      this.state.type === "charger" ? stringEnumToLabels(ChargerColum)
      : isSite ? stringEnumToLabels(SiteColum)
      : isRfid ? stringEnumToLabels(RfidColum)
      : isEvuTrend ?
        [
          TrendingColum.ENERGY,
          TrendingColum.MAX_ENERGY,
          TrendingColum.COST,
          TrendingColum.ENERGYMETER,
        ]
      : (
        ["none", Period.HOUR, "total"].includes(
          (this.state.content as TrendReportContent).aggregation,
        )
      ) ?
        stringEnumToLabels(TrendingColum).filter(
          (curItem) =>
            ![
              TrendingColum.ENERGY_AVG,
              TrendingColum.ENERGY_MIN,
              TrendingColum.ENERGY_MAX,
            ].includes(curItem),
        )
      : stringEnumToLabels(TrendingColum);
    const messagePrefix =
      isTrend ? "TrendingColumn"
      : isSite ? "SiteColumn"
      : isRfid ? "RfidColumn"
      : "ChargerColumn";
    const selectedColumns: string[] =
      // an old report without a column selection shall show all columns as selected
      isSite && !(this.state.content as SiteReportContent).columns?.length ?
        columns
      : ((this.state.content as TrendReportContent | SiteReportContent).columns ?? []);
    const columnOptions = columns.map((curItem) => ({
      value: `${curItem}`,
      label: this.context.formatMessage({ id: `${messagePrefix}_${curItem}` }),
    }));
    const changeColumns = (columns: string[]) =>
      this.setState({
        isChanged: true,
        content:
          isSite ?
            { ...(this.state.content as SiteReportContent), columns: columns as SiteColum[] }
          : { ...(this.state.content as TrendReportContent), columns: columns as TrendingColum[] },
      });

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label htmlFor="columns" sm={3}>
          <FormattedMessage id="Data" />
        </Form.Label>
        <Col sm={9}>
          {isEvuTrend ?
            <Form.Select
              id="columns"
              value={selectedColumns[0]}
              onChange={(event) => changeColumns([event.currentTarget.value])}
            >
              {columnOptions.map((curItem) => (
                <option key={curItem.value} value={curItem.value}>
                  {curItem.label}
                </option>
              ))}
            </Form.Select>
          : <CheckList
              id="columns"
              options={columnOptions}
              selected={selectedColumns}
              onChange={changeColumns}
            />
          }
        </Col>
      </Form.Group>
    );
  }

  private renderGrouping() {
    const content = this.state.content as TrendReportContent;
    const isTrend = this.state.type === "trend";
    const isEvuTrend = isTrend && (this.state.content as TrendReportContent).isEvuFormat === true;
    const groupByValues =
      isEvuTrend ? [[], ["date"], ["circuit"], ["date", "circuit"]]
      : isTrend ? [[], ["date"], ["circuit"], ["date", "circuit"], ["circuit", "date"]]
      : [];
    const groupByItems = groupByValues
      .map((curVal) => {
        if (curVal.length === 0) {
          return this.context.formatMessage(messages.groupByNone);
        }
        return curVal
          .map((curItem) =>
            this.context.formatMessage(
              curItem === "date" ? messages.groupByDate
              : curItem === "circuit" ? messages.groupByCircuit
              : curItem === "rfid" ? messages.groupByRfid
              : messages.groupByCharger,
            ),
          )
          .join(`, ${this.context.formatMessage(messages.groupByThen)} `);
      })
      .map((curString, curIndex) => {
        return (
          <option key={curIndex} value={groupByValues[curIndex]}>
            {curString}
          </option>
        );
      });
    // aggregation is used for date grouping in trend and for IOs
    // total: one sum over the whole period, trend reports only
    const aggregationItems = ["none", ...stringEnumToLabels(Period), "total"].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.context.formatMessage({ id: `Period_${curItem}` })}
        </option>
      );
    });

    return (
      <>
        {groupByItems.length > 0 && (
          <Form.Group as={Row} className="mb-3">
            <Form.Label htmlFor="groupBy" sm={3}>
              <FormattedMessage id="Group By" />
            </Form.Label>
            <Col sm={9}>
              <Form.Select
                id="groupBy"
                value={content.groupBy?.join(",")}
                onChange={(event) => {
                  const groupBy =
                    event.currentTarget.value.length ?
                      (event.currentTarget.value.split(",") as ("date" | "circuit")[])
                    : [];
                  const stateChange = {
                    isChanged: true,
                    content: {
                      ...content,
                      groupBy,
                    },
                  };
                  if (
                    content.reference?.type === "date" &&
                    !stateChange.content.groupBy?.includes("date")
                  ) {
                    stateChange.content.reference = undefined;
                  }
                  if (
                    content.reference?.type === "circuit" &&
                    !stateChange.content.groupBy?.includes("circuit")
                  ) {
                    stateChange.content.reference = undefined;
                  }
                  this.setState(stateChange);
                }}
              >
                {groupByItems}
              </Form.Select>
            </Col>
          </Form.Group>
        )}
        {
          /* this.state.type === "io" || */ content.groupBy?.includes("date") && (
            <Form.Group as={Row} className="mb-3">
              <Form.Label htmlFor="aggregation" sm={3}>
                <FormattedMessage id="Aggregation" />
              </Form.Label>
              <Col sm={9}>
                <Form.Select
                  id="aggregation"
                  value={content.aggregation}
                  onChange={(event) => {
                    this.setState({
                      isChanged: true,
                      content: {
                        ...content,
                        aggregation: event.currentTarget.value as TrendReportContent["aggregation"],
                      },
                    });
                  }}
                >
                  {aggregationItems}
                </Form.Select>
              </Col>
            </Form.Group>
          )
        }
      </>
    );
  }

  private renderTrendReference(tariffItems: JSX.Element[]) {
    const content = this.state.content as TrendReportContent;

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label sm={3}>
          <FormattedMessage id="Reference" />
        </Form.Label>
        <Col sm={9}>
          <Form.Check
            id="reference_none"
            name="reference"
            type="radio"
            className="mb-2"
            checked={content.reference === undefined}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                content: {
                  ...content,
                  reference: undefined,
                },
              });
            }}
            label={<FormattedMessage id="no reference" />}
          />
          <Form.Check
            id="reference_circuit"
            name="reference"
            type="radio"
            className="mb-2"
            disabled={!content.groupBy?.includes("circuit")}
            checked={content.reference?.type === "circuit"}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                content: {
                  ...content,
                  reference: {
                    type: "circuit",
                    ref: typeof content.reference?.ref === "number" ? content.reference.ref : 0,
                  },
                },
              });
            }}
            label={this.renderReferenceCircuitLabel(tariffItems)}
          />
          <Form.Check
            id="reference_date"
            name="reference"
            type="radio"
            disabled={!content.groupBy?.includes("date")}
            checked={content.reference?.type === "date"}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                content: {
                  ...content,
                  reference: {
                    type: "date",
                    ref: Period.DAY,
                  },
                },
              });
            }}
            label={this.renderReferenceDateLabel()}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderReferenceCircuitLabel(tariffItems: JSX.Element[]) {
    const content = this.state.content as TrendReportContent;
    return (
      <>
        <FormattedMessage id="other tariff circuit" />
        {content.reference?.type === "circuit" && (
          <>
            <Form.Select
              value={content.reference.ref}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  content: {
                    ...content,
                    reference: {
                      type: "circuit",
                      ref: parseInt(event.currentTarget.value),
                    },
                  },
                });
              }}
            >
              {tariffItems}
            </Form.Select>
          </>
        )}
      </>
    );
  }

  private renderReferenceDateLabel() {
    const content = this.state.content as TrendReportContent;
    const periodRefItems = [Period.YEAR, Period.MONTH, Period.DAY].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.context.formatMessage({ id: `Period_${curItem}` })}
        </option>
      );
    });
    return (
      <>
        <FormattedMessage id="previous time period" />
        {content.reference?.type === "date" && (
          <>
            <Form.Select
              value={content.reference.ref}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  content: {
                    ...content,
                    reference: {
                      type: "date",
                      ref: event.currentTarget.value as Period,
                    },
                  },
                });
              }}
            >
              {periodRefItems}
            </Form.Select>
          </>
        )}
      </>
    );
  }

  private renderFilter(
    filterOptions?: ICheckListOption<number>[],
    filterOptions2?: ICheckListOption<number>[],
  ) {
    const isTrend = this.state.type === "trend";
    const isIO = this.state.type === "io";
    const isCharger = this.state.type === "charger";
    const content = this.state.content as TrendReportContent;
    const isCircuitListOpen =
      content.filter.some((curItem) => curItem.type === "circuit") || this.state.isCircuitListOpen;

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label sm={3}>
          <FormattedMessage id="Filter" />
        </Form.Label>
        <Col sm={9}>
          {isTrend && isSet(filterOptions) && (
            <>
              <Form.Check
                id="filterCircuit"
                className="mb-2"
                checked={isCircuitListOpen}
                onChange={(event) => {
                  const filter = content.filter.filter(
                    (curItem) => !["circuit", "input"].includes(curItem.type),
                  );
                  if (event.currentTarget.checked) {
                    // select all circuits as default, to be narrowed down by the user
                    filterOptions.forEach((curOption) =>
                      filter.push({ type: "circuit", ref: curOption.value }),
                    );
                  }
                  this.setState({
                    isChanged: true,
                    content: { ...content, filter },
                    isCircuitListOpen: event.currentTarget.checked,
                  });
                }}
                label={this.renderFilterCircuitCaption("circuit")}
              />
              {isCircuitListOpen &&
                this.renderFilterCircuitList("circuit", filterOptions, "ms-4 mb-2")}
            </>
          )}

          {isIO && isSet(filterOptions) && isSet(filterOptions2) && (
            <>
              <Form.Check
                id="filterCircuit"
                className="mb-2"
                checked={
                  content.filter.filter((curItem) => ["output", "input"].includes(curItem.type))
                    .length > 0
                }
                onChange={(event) => {
                  const filter = content.filter.filter(
                    (curItem) => !["output", "input"].includes(curItem.type),
                  );
                  if (event.currentTarget.checked) {
                    // select all inputs and outputs as default, to be narrowed down by the user
                    filterOptions.forEach((curOption) =>
                      filter.push({ type: "input", ref: curOption.value }),
                    );
                    filterOptions2.forEach((curOption) =>
                      filter.push({ type: "output", ref: curOption.value }),
                    );
                  }
                  this.setState({
                    isChanged: true,
                    content: { ...content, filter },
                    emptyInputFilter: false,
                    emptyOutputFilter: false,
                  });
                }}
                label={
                  <div className="d-flex">
                    <FormattedMessage id="IOs" />
                    {this.state.emptyInputFilter === true &&
                      this.state.emptyOutputFilter === true && (
                        <div className="ms-2 modal-error-text">
                          <FormattedMessage id="No Input/Output selected!" />
                        </div>
                      )}
                  </div>
                }
              />
              {content.filter.filter((curItem) => ["output", "input"].includes(curItem.type))
                .length > 0 && (
                <div className="ms-3">
                  {this.renderFilterCircuitCaption("input")}
                  {this.renderFilterCircuitList("input", filterOptions, "mb-2")}
                  {this.renderFilterCircuitCaption("output")}
                  {this.renderFilterCircuitList("output", filterOptions2, "mb-2")}
                </div>
              )}
            </>
          )}

          <Form.Check
            id="filterBegin"
            className="mb-2"
            checked={content.filter.filter((curItem) => curItem.type === "begin").length > 0}
            onChange={(event) => {
              const filter = content.filter.filter((curItem) => curItem.type !== "begin");
              if (event.currentTarget.checked) {
                filter.push({ type: "begin", ref: Math.round(Date.now() / 1000) });
              }
              this.setState({
                isChanged: true,
                content: { ...content, filter },
              });
            }}
            label={this.renderFilterBeginLabel()}
          />
          <Form.Check
            id="filterEnd"
            className="mb-2"
            checked={content.filter.filter((curItem) => curItem.type === "end").length > 0}
            onChange={(event) => {
              const filter = content.filter.filter((curItem) => curItem.type !== "end");
              if (event.currentTarget.checked) {
                filter.push({ type: "end", ref: Math.round(Date.now() / 1000) });
              }
              this.setState({ isChanged: true, content: { ...content, filter } });
            }}
            label={this.renderFilterEndLabel()}
          />

          <Form.Check
            id="filterPeriod"
            className="mb-2"
            checked={content.filter.filter((curItem) => curItem.type === "period").length > 0}
            onChange={(event) => {
              const filter = content.filter.filter((curItem) => curItem.type !== "period");
              if (event.currentTarget.checked) {
                filter.push({ type: "period", ref: Period.MONTH });
              }
              this.setState({ isChanged: true, content: { ...content, filter } });
            }}
            label={this.renderFilterPeriodLabel()}
          />

          {isTrend && (
            <Form.Check
              id="hideEmpty"
              className="mb-2"
              checked={content.hideEmpty}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  content: { ...content, hideEmpty: event?.currentTarget.checked },
                });
              }}
              label={<FormattedMessage id="hide zero data values" />}
            />
          )}

          {isCharger && this.renderChargerFilters()}
        </Col>
      </Form.Group>
    );
  }

  private renderFilterCircuitCaption(inFilter: "circuit" | "input" | "output") {
    const content = this.state.content as TrendReportContent;
    const filterCount =
      (
        (inFilter === "input" && this.state.emptyInputFilter) ||
        (inFilter === "output" && this.state.emptyOutputFilter)
      ) ?
        0
      : content.filter.filter((curItem) => curItem.type === inFilter).length;
    return (
      <>
        <FormattedMessage
          id={`${
            inFilter === "circuit" ? messages.tariffCircuits.id
            : inFilter === "input" ? messages.inputs.id
            : messages.outputs.id
          }`}
        />
        {` (${filterCount})`}
      </>
    );
  }

  private renderFilterCircuitList(
    inFilter: "circuit" | "input" | "output",
    options: ICheckListOption<number>[],
    className: string,
  ) {
    const content = this.state.content as TrendReportContent;
    return (
      <CheckList
        id={`filter_${inFilter}`}
        className={className}
        options={options}
        selected={content.filter
          .filter((curItem) => curItem.type === inFilter)
          .map((curItem) => curItem.ref as number)}
        onChange={(refs) => {
          let emptyInputFilter = this.state.emptyInputFilter;
          let emptyOutputFilter = this.state.emptyOutputFilter;
          const filter = content.filter.filter((curItem) => curItem.type !== inFilter);

          if (inFilter !== "circuit") {
            // do not alow empty IO filter, use a placeholder reference instead
            inFilter === "input" ?
              (emptyInputFilter = refs.length === 0)
            : (emptyOutputFilter = refs.length === 0);

            if (refs.length === 0) {
              refs = [((inFilter === "input" ? 1 : 0) << 31) + 0x7f000000 + 0xffff00 + 0xff];
            }
          }
          refs.forEach((curRef) => {
            filter.push({ type: inFilter, ref: curRef });
          });

          this.setState({
            isChanged: true,
            content: { ...content, filter },
            emptyInputFilter,
            emptyOutputFilter,
          });
        }}
      />
    );
  }

  private renderFilterBeginLabel() {
    const content = this.state.content as TrendReportContent;
    return (
      <>
        <FormattedMessage id="start date" />
        {content.filter.filter((curItem) => curItem.type === "begin").length > 0 && (
          <>
            :{" "}
            <DateInput
              type="date"
              className="mb-2"
              style={{ display: "inline-block", marginTop: -5 }}
              valueMs={
                (content.filter.find((curItem) => curItem.type === "begin")!.ref as number) * 1000
              }
              onChange={(valueMs) => {
                const filter = content.filter.map((curItem) =>
                  curItem.type === "begin" ? { ...curItem, ref: valueMs / 1000 } : curItem,
                );
                this.setState({ isChanged: true, content: { ...content, filter } });
              }}
            />
          </>
        )}
      </>
    );
  }

  private renderFilterEndLabel() {
    const content = this.state.content as ChargerReportContent;
    return (
      <>
        <FormattedMessage id="end date" />
        {content.filter.filter((curItem) => curItem.type === "end").length > 0 && (
          <>
            :{" "}
            <DateInput
              type="date"
              endOfDay
              className="mb-2"
              style={{ display: "inline-block", marginTop: -5 }}
              valueMs={
                (content.filter.find((curItem) => curItem.type === "end")!.ref as number) * 1000
              }
              onChange={(valueMs) => {
                const filter = content.filter.map((curItem) =>
                  curItem.type === "end" ? { ...curItem, ref: valueMs / 1000 } : curItem,
                );
                this.setState({ isChanged: true, content: { ...content, filter } });
              }}
            />
          </>
        )}
      </>
    );
  }

  private renderFilterPeriodLabel() {
    const content = this.state.content as TrendReportContent;
    const periodItems = [
      ExtendedPeriod.LAST_DAY,
      Period.DAY,
      ExtendedPeriod.LAST_MONTH,
      Period.MONTH,
      ExtendedPeriod.LAST_YEAR,
      Period.YEAR,
    ].map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.context.formatMessage({ id: `FilterPeriod_${curItem}` })}
        </option>
      );
    });

    return (
      <>
        <FormattedMessage id="period" />
        {content.filter.filter((curItem) => curItem.type === "period").length > 0 && (
          <>
            :{" "}
            <Form.Select
              className="mb-2"
              style={{ display: "inline-block" }}
              value={content.filter.find((curItem) => curItem.type === "period")!.ref}
              onChange={(event) => {
                const filter = content.filter.filter((curItem) => curItem.type !== "period")!;
                filter.push({ type: "period", ref: event.currentTarget.value as Period });
                this.setState({ isChanged: true, content: { ...content, filter } });
              }}
            >
              {periodItems}
            </Form.Select>
          </>
        )}
      </>
    );
  }

  private renderChargerFilters() {
    const content = this.state.content as ChargerReportContent;
    const isUserChecked = content.filter.some((curItem) => curItem.type === "rfiduser");
    const userOptions = this.props.rfids
      .filter((anRfid) => anRfid.isActive)
      .sort((a, b) => {
        const keyA = a.name ? a.name : a.rfid;
        const keyB = b.name ? b.name : b.rfid;
        return isSet(keyA) && isSet(keyB) && keyA > keyB ? 1 : -1;
      })
      .map((anRfid) => ({
        value: anRfid.rfid,
        label: anRfid.name ? `${anRfid.name} [${anRfid.rfid}]` : anRfid.rfid,
      }));

    return (
      <>
        {this.renderCategoryFilter()}
        <Form.Check
          id="filterRFIDUser"
          className="mb-2"
          checked={isUserChecked}
          onChange={(event) => {
            const filter = content.filter.filter((curItem) => curItem.type !== "rfiduser");
            if (event.currentTarget.checked) {
              // entry without value just marks the filter as active
              filter.push({ type: "rfiduser", value: "" });
            }
            this.setState({ isChanged: true, content: { ...content, filter } });
          }}
          label={<FormattedMessage id="ChargerColumn_user" />}
        />
        {isUserChecked && (
          <CheckList
            id="filterRFIDUsers"
            className="ms-4 mb-2"
            options={userOptions}
            selected={content.filter
              .filter((curItem) => curItem.type === "rfiduser" && curItem.value)
              .map((curItem) => curItem.value!)}
            onChange={(users) => {
              const filter: IChargerReportFilter[] = [
                ...content.filter.filter(
                  (curItem) => curItem.type !== "rfiduser" || curItem.value === "",
                ),
                ...users.map((value) => ({ type: "rfiduser" as const, value })),
              ];
              this.setState({ isChanged: true, content: { ...content, filter } });
            }}
          />
        )}
        {this.renderChargerFilter()}
      </>
    );
  }

  private renderChargerFilter() {
    const content = this.state.content as ChargerReportContent;
    const isChecked = content.filter.some((curItem) => curItem.type === "charger");
    const chargerOptions = this.props.chargers
      .filter((curItem) => isSet(curItem.fk_charger))
      .sort((a, b) => (a.name ?? a.chargepoint).localeCompare(b.name ?? b.chargepoint))
      .map((curItem) => ({
        value: curItem.fk_charger,
        label: curItem.name ?? curItem.chargepoint,
      }));

    return (
      <>
        <Form.Check
          id="filterCharger"
          className="mb-2"
          checked={isChecked}
          onChange={(event) => {
            const filter = content.filter.filter((curItem) => curItem.type !== "charger");
            if (event.currentTarget.checked) {
              // entry without charger reference just marks the filter as active
              filter.push({ type: "charger" });
            }
            this.setState({ isChanged: true, content: { ...content, filter } });
          }}
          label={<FormattedMessage id="Chargers" />}
        />
        {isChecked && (
          <CheckList
            id="filterChargers"
            className="ms-4 mb-2"
            options={chargerOptions}
            selected={content.filter
              .filter((curItem) => curItem.type === "charger" && isSet(curItem.ref))
              .map((curItem) => curItem.ref as number)}
            onChange={(chargerIds) => {
              const filter: IChargerReportFilter[] = [
                ...content.filter.filter((curItem) => curItem.type !== "charger"),
                { type: "charger" },
                ...chargerIds.map((ref) => ({ type: "charger" as const, ref })),
              ];
              this.setState({ isChanged: true, content: { ...content, filter } });
            }}
          />
        )}
      </>
    );
  }

  // the category filter of charger and rfid reports
  private renderCategoryFilter() {
    const content = this.state.content as ChargerReportContent;
    return (
      <Form.Check
        id="filterCategories"
        className="mb-2"
        checked={content.filter.filter((curItem) => ["category"].includes(curItem.type)).length > 0}
        onChange={(event) => {
          const filter = content.filter.filter((curItem) => !["category"].includes(curItem.type));
          if (event.currentTarget.checked) {
            filter.push({ type: "category", value: "" });
          }
          this.setState({
            isChanged: true,
            content: { ...content, filter: filter as IChargerReportFilter[] },
          });
        }}
        label={
          <>
            <FormattedMessage id="Category" />
            {content.filter.filter((curItem) => curItem.type === "category").length > 0 && (
              <Form.Control
                type="text"
                className="mb-2"
                value={content.filter
                  .filter((curItem) => curItem.type === "category")
                  .map((curItem) => `${curItem.value}`)}
                onChange={(event) => {
                  const filter = content.filter.filter((curItem) => curItem.type !== "category");
                  filter.push({ type: "category", value: event.target.value });
                  this.setState({ isChanged: true, content: { ...content, filter } });
                }}
              ></Form.Control>
            )}
          </>
        }
      />
    );
  }

  private renderRfidSettings() {
    return (
      <>
        {this.renderDataColumns()}
        <Form.Group as={Row} className="mb-3">
          <Form.Label sm={3}>
            <FormattedMessage id="Filter" />
          </Form.Label>
          <Col sm={9}>{this.renderCategoryFilter()}</Col>
        </Form.Group>
      </>
    );
  }

  private renderChargerSettings() {
    return (
      <>
        {this.renderDataColumns()}
        {this.renderFilter()}
      </>
    );
  }

  private renderIoSettings() {
    const inputIndexes = Array.from(Array(this.props.input.length).keys())
      .filter(
        // check if id < 10000 which indicates its on our own site and not a virutal input!
        (curId) => (this.props.input[curId].id ?? 0) < 10000 && this.props.input[curId].webActive,
      )
      .sort((a, b) => this.props.input[a].name.localeCompare(this.props.input[b].name));

    const inputOptions = inputIndexes.map((curIndex) => {
      const curInput = this.props.input[curIndex];
      const curBus = getBusNr(curInput.bus);
      return {
        value: prepareIOBusModuleChannel(true, curBus, curInput.module, curInput.channel),
        label: curInput.name,
      };
    });

    const outputIndexes = Array.from(Array(this.props.output.length).keys())
      .filter(
        // check if id < 10000 which indicates its on our own site and not a virutal input!
        (curId) => (this.props.output[curId].id ?? 0) < 10000 && this.props.output[curId].webActive,
      )
      .sort((a, b) => this.props.output[a].name.localeCompare(this.props.output[b].name));

    const outputOptions = outputIndexes.map((curIndex) => {
      const curOutput = this.props.output[curIndex];
      const curBus = getBusNr(curOutput.bus);
      return {
        value: prepareIOBusModuleChannel(false, curBus, curOutput.module, curOutput.channel),
        label: curOutput.name,
      };
    });

    return (
      <>
        {this.renderGrouping()}
        {this.renderFilter(inputOptions, outputOptions)}
      </>
    );
  }

  private renderSiteSettings() {
    const companyOptions = this.props.companyList.map((company) => (
      <option key={company.id} value={company.id}>
        {company.name}
      </option>
    ));

    return (
      <>
        <Form.Group as={Row} className="mt-1">
          <Form.Label sm={3}>
            <FormattedMessage id="Company" />
          </Form.Label>
          <Col sm={9}>
            {/* like the report type, the company scope is fixed on creation */}
            {isSet(this.props.reportId) ?
              <span>
                {
                  this.props.companyList.find((curItem) => curItem.id === this.state.companyId)
                    ?.name
                }
              </span>
            : <Form.Select
                id="company"
                value={this.state.companyId ?? ""}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  this.setState({
                    isChanged: true,
                    companyId: value ? parseInt(value, 10) : this.props.userCompanyId,
                  });
                }}
              >
                {companyOptions}
              </Form.Select>
            }
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mt-1">
          <Col sm={9}>
            <Form.Check
              id="includeSubcompanies"
              className="mt-3 mb-2"
              checked={(this.state.content as SiteReportContent).includeSubcompanies ?? false}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  content: {
                    ...this.state.content,
                    includeSubcompanies: event?.currentTarget.checked,
                  },
                });
              }}
              label={<FormattedMessage id="Include Subcompanies" />}
            />
          </Col>
        </Form.Group>
        {this.renderDataColumns()}
      </>
    );
  }

  private renderFileFormat() {
    const fileFormats: NonNullable<Report["fileFormat"]>[] = ["xlsx", "xls", "ods", "csv"];
    const fileformatItems = fileFormats.map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.context.formatMessage({ id: `ReportFormat_${curItem}` })}
        </option>
      );
    });

    return (
      <Form.Group className="mb-2 d-flex align-items-center">
        <Form.Label htmlFor="fileFormat" className="mb-0" style={compactLabelStyle}>
          <FormattedMessage id="Output Format" />
        </Form.Label>
        <Form.Select
          id="fileFormat"
          className="w-auto"
          value={this.state.fileFormat ?? fileFormats[0]}
          onChange={(event) => {
            this.setState({
              isChanged: true,
              fileFormat: event.currentTarget.value as Report["fileFormat"],
            });
          }}
        >
          {fileformatItems}
        </Form.Select>
      </Form.Group>
    );
  }

  private renderCSVDelimiter() {
    const delimiters: NonNullable<Report["csvDelimiter"]>[] = [",", ";", "\t", "|"];
    const delimiterItems = delimiters.map((curItem) => {
      return (
        <option key={`${curItem}`} value={`${curItem}`}>
          {this.context.formatMessage({ id: `ReportCSVDelimiter_${curItem}` })}
        </option>
      );
    });

    return (
      <Form.Group className="mb-2 d-flex align-items-center">
        <Form.Label htmlFor="csvDelimiter" className="mb-0" style={compactLabelStyle}>
          <FormattedMessage id="Delimiter" />
        </Form.Label>
        <Form.Select
          id="csvDelimiter"
          className="w-auto"
          value={this.state.csvDelimiter ?? delimiters[0]}
          onChange={(event) => {
            this.setState({
              isChanged: true,
              csvDelimiter: event.currentTarget.value as Report["csvDelimiter"],
            });
          }}
        >
          {delimiterItems}
        </Form.Select>
      </Form.Group>
    );
  }

  private renderHideSumRowCheckbox() {
    return (
      <Col sm={9}>
        <Form.Check
          id="hideSumRow"
          className="mt-2 mb-2"
          checked={this.state.hideSumRow ?? false}
          onChange={(event) => {
            this.setState({
              isChanged: true,
              hideSumRow: event?.currentTarget.checked,
            });
          }}
          label={<FormattedMessage id="Hide Sum Row" />}
        />
      </Col>
    );
  }

  private renderExport() {
    const periodItems = ["none", Period.HOUR, Period.DAY, Period.MONTH, Period.YEAR].map(
      (curItem) => {
        return (
          <option key={`${curItem}`} value={`${curItem}`}>
            {this.context.formatMessage({ id: `AutoexportPeriod_${curItem}` })}
          </option>
        );
      },
    );

    return (
      <Form.Group className="mt-3 mb-3">
        <Form.Label>
          <FormattedMessage id="Auto-Export" />
        </Form.Label>
        <div className="d-flex align-items-center">
          <Form.Label htmlFor="exportPeriod" className="mb-0" style={compactLabelStyle}>
            <FormattedMessage id="Period" />
          </Form.Label>
          <Form.Select
            id="exportPeriod"
            className="w-auto"
            value={this.state.exportInterval}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                exportInterval: event.currentTarget.value as Period | "none",
              });
            }}
          >
            {periodItems}
          </Form.Select>
        </div>
        {this.state.exportInterval !== "none" && this.renderRecipients()}
      </Form.Group>
    );
  }

  // without recipients the report is sent to the email address of its owner
  private renderRecipients() {
    const hasRecipients = isSet(this.state.recipients);

    return (
      <Form.Group className="mt-2">
        <Form.Label htmlFor="exportTarget">
          <FormattedMessage id="recipient" />
        </Form.Label>
        :{" "}
        <>
          {!hasRecipients && <span>{this.props.userEmail}</span>}
          <Form.Check
            id="exportOtherRecipients"
            checked={hasRecipients}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                recipients: event.currentTarget.checked ? "" : undefined,
              });
            }}
            label={<FormattedMessage id="Use other email address" />}
          />
          {hasRecipients && (
            <>
              <Form.Control
                id="exportTarget"
                className={
                  this.areAllTargetsValid(this.state.recipients) ? "mt-2" : "mt-2 input-error"
                }
                autoComplete="off"
                placeholder={this.context.formatMessage(messages.exportTargetHint)}
                type="text"
                value={this.state.recipients}
                onChange={(event) => {
                  this.setState({
                    isChanged: true,
                    recipients: event.currentTarget.value.replace(/;/g, ","),
                  });
                }}
              />
              <Form.Text className="text-danger">
                <FormattedMessage id="Using other email addresses is not recommended:" />
                &nbsp;
                <FormattedMessage id="Other recipients cannot open or edit the report in enline cloud." />
              </Form.Text>
            </>
          )}
        </>
      </Form.Group>
    );
  }

  private makeEmptyReport(reportType: Report["type"]) {
    const content =
      reportType === undefined || reportType === "trend" ?
        ({
          groupBy: ["date", "circuit"],
          aggregation: Period.MONTH,
          columns: [TrendingColum.ENERGY],
          filter: [],
          reference: undefined,
          hideEmpty: true,
        } as TrendReportContent)
      : reportType === "charger" ?
        ({
          groupBy: [],
          columns: [ChargerColum.ENERGY, ChargerColum.DURATION],
          filter: [],
        } as ChargerReportContent)
      : reportType === "io" ?
        ({
          aggregation: Period.MONTH,
          filter: [],
        } as IoReportContent)
      : reportType === "rfid" ?
        ({
          columns: [
            RfidColum.NAME,
            RfidColum.LAST_USAGE,
            RfidColum.USAGE_COUNT,
            RfidColum.IS_ACTIVE,
          ],
          filter: [],
        } as RfidReportContent)
      : ({
          filter: [],
          includeSubcompanies: true,
          columns: [SiteColum.COMPANY_INFO, SiteColum.USAGE_INFO, SiteColum.DEVICE_INFO],
        } as SiteReportContent);

    // this must list all report fields, as it is merged into the state of a previously
    // edited report: a leftover id would store the new report over the old one
    // cross-site reports are scoped to a company, all others to the current site
    return {
      id: undefined,
      userId: this.props.userId,
      siteId: reportType === "site" ? undefined : this.props.siteId,
      companyId: reportType === "site" ? this.props.userCompanyId : undefined,
      title: this.context.formatMessage(messages.titleDefault),
      isActive: true,
      type: reportType,
      content,
      emptyInputFilter: false,
      emptyOutputFilter: false,
      exportInterval: "none",
      recipients: undefined,
      fileFormat: undefined,
      csvDelimiter: undefined,
      hideSumRow: false,
    } as ChangedReport;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ModalReportSettingsItemComponent);
